import React, { useState, useEffect, useMemo, useRef, useCallback } from "react";
import ReactApexChart from "react-apexcharts";
import api from "../utils/api";
import ChartBurgerMenu from "./ChartBurgerMenu";
import AIAnalyzerModal from "./AIAnalyzerModal";
import AIAnalyzerButton from "./AIAnalyzerButton";
import BoltLoader from "./BoltLoader";
import { buildChartPayload } from "../utils/aiAnalyzerEngine";
import {
  formatAmount,
  rateForCategory,
  resolveTariffs,
  unitLabel,
} from "../utils/currencyConversion";

const LINE_COLORS = [
  "#10b981", "#3D5DAA", "#f59e0b", "#ef4444", "#8b5cf6",
  "#1e40af", "#14b8a6", "#f97316", "#6366f1", "#1BA08E",
  "#06b6d4", "#d946ef", "#ea580c", "#7c3aed", "#0da5c4",
  "#059669", "#7c2d12", "#ec4899", "#be185d", "#0369a1",
  "#b91c1c", "#5b21b6", "#165e63", "#713f12", "#64748b",
  "#78716c", "#44403c", "#1f2937", "#374151", "#475569",
];

// Generate a consistent color index from a date string (same date always gets same color)
const getColorIndexFromDate = (dateStr) => {
  let hash = 0;
  for (let i = 0; i < dateStr.length; i++) {
    const char = dateStr.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash = hash & hash; // Convert to 32bit integer
  }
  return Math.abs(hash) % LINE_COLORS.length;
};

const getYesterday = () => {
  const d = new Date();
  d.setDate(d.getDate() - 1);
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};

const formatTimeOnlyLabel = (value) => {
  if (value == null) return "";
  const text = String(value).trim();

  const dateTimeMatch = text.match(/^(\d{4}-\d{2}-\d{2})[ T](\d{2}:\d{2}(?::\d{2})?)$/);
  if (dateTimeMatch) return dateTimeMatch[2];

  const timeOnlyMatch = text.match(/^(\d{2}:\d{2}(?::\d{2})?)$/);
  if (timeOnlyMatch) return timeOnlyMatch[1];

  return text;
};

const extractDateOnlyLabel = (value) => {
  if (value == null) return "";
  const text = String(value).trim();
  const match = text.match(/^(\d{4}-\d{2}-\d{2})[ T]\d{2}:\d{2}(?::\d{2})?$/);
  if (match) return match[1];
  const dateOnlyMatch = text.match(/^(\d{4}-\d{2}-\d{2})$/);
  return dateOnlyMatch ? dateOnlyMatch[1] : text;
};

const sumNumericValues = (values = []) =>
  values.reduce((total, value) => {
    const numericValue = Number(value);
    return Number.isFinite(numericValue) ? total + numericValue : total;
  }, 0);

const roundToNiceStep = (value) => {
  if (!Number.isFinite(value) || value <= 0) return 1;
  const magnitude = Math.pow(10, Math.floor(Math.log10(value)) || 0);
  const normalized = value / magnitude;
  let nice;
  if (normalized <= 1) nice = 1;
  else if (normalized <= 2) nice = 2;
  else if (normalized <= 5) nice = 5;
  else nice = 10;
  return nice * magnitude;
};

const nextCoarserNiceStep = (step) => {
  const magnitude = Math.pow(10, Math.floor(Math.log10(step)) || 0);
  const normalized = step / magnitude;
  if (normalized < 2) return 2 * magnitude;
  if (normalized < 5) return 5 * magnitude;
  if (normalized < 10) return 10 * magnitude;
  return roundToNiceStep(step * 2);
};

/** At most `maxLabels` y-axis labels; fewer when the range is small. */
const computeYAxisScale = (maxVal, maxLabels = 5) => {
  const fallback = { max: 10, stepSize: 5 };

  if (!Number.isFinite(maxVal) || maxVal <= 0) {
    return fallback;
  }

  const padded = maxVal * 1.05;
  let step = roundToNiceStep(padded / Math.max(maxLabels - 1, 1));

  for (let attempt = 0; attempt < 12; attempt++) {
    const axisMax = Math.ceil(padded / step) * step;
    const labelCount = Math.floor(axisMax / step) + 1;
    if (labelCount <= maxLabels) {
      return {
        max: axisMax,
        stepSize: step,
      };
    }
    step = nextCoarserNiceStep(step);
    if (step >= padded * 2) break;
  }

  const axisMax = Math.ceil(padded);
  const stepSize = roundToNiceStep(axisMax / Math.max(maxLabels - 1, 1));
  return {
    max: Math.ceil(axisMax / stepSize) * stepSize,
    stepSize,
  };
};

const isAllowedYTick = (value, stepSize, axisMax) => {
  if (!Number.isFinite(value) || !Number.isFinite(stepSize) || stepSize <= 0) {
    return true;
  }
  if (value < 0 || value > axisMax + stepSize * 0.001) return false;
  const ratio = value / stepSize;
  return Math.abs(ratio - Math.round(ratio)) < 1e-6;
};

const extractEnergyValue = (dayData = {}, machineData = {}, fallbackValues = []) => {
  const directCandidates = [
    machineData?.energy,
    machineData?.energy_kwh,
    machineData?.energyKwh,
    machineData?.energy_kWh,
    machineData?.total_energy,
    machineData?.totalEnergy,
    dayData?.energy,
    dayData?.energy_kwh,
    dayData?.energyKwh,
    dayData?.energy_kWh,
  ];

  for (const candidate of directCandidates) {
    const numericValue = Number(candidate);
    if (Number.isFinite(numericValue)) {
      return numericValue;
    }
  }

  if (Array.isArray(machineData?.energy) || Array.isArray(dayData?.energy)) {
    const arrayValues = Array.isArray(machineData?.energy)
      ? machineData.energy
      : dayData.energy;
    return sumNumericValues(arrayValues);
  }

  return sumNumericValues(fallbackValues);
};

const flattenHierarchyNodes = (nodes) => {
  if (!nodes) return [];
  const list = Array.isArray(nodes) ? nodes : Object.values(nodes);
  let flat = [];
  list.forEach((node) => {
    if (node?.machine_name) {
      flat.push({ name: node.machine_name, category: node.category || "" });
    }
    const children = node?.children;
    if (children) {
      flat = flat.concat(
        flattenHierarchyNodes(Array.isArray(children) ? children : Object.values(children)),
      );
    }
  });
  return flat;
};

const ComparisonGraph = ({
  machinesList = [],
  title = "Machine 1",
  loaderKey,
  onLoadingChange,
  currencyMode = "Energy",
  tariffs,
}) => {
  const isPkr = currencyMode === "PKR";
  // HistoricalTab passes the resolved set; fall back so the graph still works
  // if it is ever mounted standalone.
  const rates = useMemo(() => tariffs || resolveTariffs(null), [tariffs]);
  // The plotted series is power (kW); in PKR it is costed the same way the
  // main Historical chart costs power, giving a per-hour spend rate.
  const seriesUnit = isPkr ? "PKR" : "kW";
  const energyUnit = unitLabel(isPkr);
  const chartRef = useRef(null);
  const [isFullscreen, setIsFullscreen] = useState(false);

  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(
        document.fullscreenElement === chartRef.current ||
        document.webkitFullscreenElement === chartRef.current
      );
    };

    document.addEventListener("fullscreenchange", handleFullscreenChange);
    document.addEventListener("webkitfullscreenchange", handleFullscreenChange);

    return () => {
      document.removeEventListener("fullscreenchange", handleFullscreenChange);
      document.removeEventListener("webkitfullscreenchange", handleFullscreenChange);
    };
  }, []);

  const [selectedMachine, setSelectedMachine] = useState("");
  const [selectedDates, setSelectedDates] = useState([getYesterday()]);
  const [isCalendarOpen, setIsCalendarOpen] = useState(false);
  const [calendarMonth, setCalendarMonth] = useState(() => {
    const y = new Date(); y.setDate(y.getDate() - 1);
    return { year: y.getFullYear(), month: y.getMonth() };
  });
  const [chartData, setChartData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [startHour, setStartHour] = useState(0);
  const lastFetchKey = useRef(null);
  const inFlightFetch = useRef(false);
  const hasAutoLoadedRef = useRef(false);
  const [isStartOpen, setIsStartOpen] = useState(false);
  const startRef = useRef(null);
  const [hierarchyTree, setHierarchyTree] = useState(null);

  useEffect(() => {
    const username = localStorage.getItem("username");
    if (!username) return;
    api
      .get("/hierarchy-tree", { params: { username } })
      .then((res) => setHierarchyTree(res.data || null))
      .catch(() => setHierarchyTree(null));
  }, []);

  const loadOptions = useMemo(() => {
    const seen = new Set();
    const options = [];

    const addOption = (name, category = "") => {
      if (!name || seen.has(name)) return;
      const cat = (category || "").toUpperCase();
      if (cat === "ENVIRONMENT") return;
      seen.add(name);
      options.push({ name, category: cat });
    };

    // Prefer the (branch-scoped) machinesList from the parent so the selector
    // lists only the machines under the selected branch. Fall back to the full
    // hierarchy tree only when no list was provided.
    if (machinesList && machinesList.length) {
      machinesList.forEach((m) => addOption(m.name, m.category));
    } else {
      addOption("Usage", "Special");
      if (hierarchyTree?.children) {
        const roots = Array.isArray(hierarchyTree.children)
          ? hierarchyTree.children
          : Object.values(hierarchyTree.children);
        flattenHierarchyNodes(roots).forEach((node) => {
          addOption(node.name, node.category);
        });
      }
    }

    const getPriority = (cat) => {
      if (cat === "SPECIAL") return -1;
      if (cat === "GRID") return 0;
      if (cat === "GENERATOR") return 1;
      if (cat === "SOLAR") return 2;
      if (cat === "MACHINE") return 3;
      return 4;
    };

    return options.sort((a, b) => {
      if (a.name === "Usage") return -1;
      if (b.name === "Usage") return 1;
      const priorityDiff = getPriority(a.category) - getPriority(b.category);
      if (priorityDiff !== 0) return priorityDiff;
      return a.name.localeCompare(b.name);
    });
  }, [hierarchyTree, machinesList]);

  const [aiModalOpen, setAiModalOpen] = useState(false);
  const [aiModalPayload, setAiModalPayload] = useState(null);

  const handleAnalyzeComparisonGraph = () => {
    const chartTitle = `${title} - ${selectedMachine || "Comparison"}`;
    const datasets = (series || []).map((s) => ({
      label: s.name,
      data: s.data,
    }));
    const timestamps = (chartData?.time_labels || []).map((t) => formatTimeOnlyLabel(t));

    const payload = buildChartPayload({
      chartTitle: chartTitle,
      metricName: isPkr ? "Power Cost Comparison" : "Power Comparison",
      unit: seriesUnit,
      timestamps: timestamps,
      datasets: datasets,
      dateRange: { start: selectedDates.join(", ") },
    });
    setAiModalPayload(payload);
    setAiModalOpen(true);
  };

  useEffect(() => {
    if (!onLoadingChange || !loaderKey) return;
    onLoadingChange(loaderKey, loading);
    return () => onLoadingChange(loaderKey, false);
  }, [loaderKey, loading, onLoadingChange]);

  // A genset or solar node bills at its own tariff, so the rate is picked from
  // the selected machine's category rather than always using the grid price.
  const selectedMachineCategory = useMemo(
    () =>
      loadOptions.find((option) => option.name === selectedMachine)?.category ||
      "",
    [loadOptions, selectedMachine],
  );

  const unitPrice = useMemo(
    () => rateForCategory(selectedMachineCategory, rates),
    [selectedMachineCategory, rates],
  );

  // Default to first machine when list loads
  useEffect(() => {
    if (loadOptions.length > 0 && !selectedMachine) {
      const preferred = loadOptions.find((m) => m.name === title);
      if (preferred) {
        setSelectedMachine(preferred.name);
        return;
      }

      const first = loadOptions.find((m) => m.name !== "Usage") || loadOptions[0];
      if (first) setSelectedMachine(first.name);
    }
  }, [loadOptions, selectedMachine, title]);

  // Fetch comparison data manually (called by button, not automatic)
  const fetchComparisonData = useCallback(async (force = false, hourOverride = null) => {
    if (!selectedMachine || selectedDates.length === 0) return;
    // Fetch key is based on machine and dates only (startHour is sent to API but doesn't affect cache key)
    const fetchKey = `${selectedMachine}|${selectedDates.join(",")}`;

    // If an identical request is already in-flight, skip starting another unless forced.
    if (!force && inFlightFetch.current && lastFetchKey.current === fetchKey) {
      console.log("⏭️ Skipping duplicate API call (already in-flight)");
      return;
    }

    // mark this as the last requested key
    lastFetchKey.current = fetchKey;

    const username = localStorage.getItem("username");
    if (!username) return;

    if (force) {
      setChartData(null);
    }

    setLoading(true);
    setError("");
    try {
      inFlightFetch.current = true;
      const params = new URLSearchParams();
      params.append("username", username);
      selectedDates.forEach((d) => params.append("date", d));
      // Use hourOverride if provided (from refetch), otherwise use current startHour
      const effectiveHour = hourOverride !== null ? hourOverride : startHour;
      const startTimeParam = `${String(effectiveHour).padStart(2, "0")}:00:00`;
      params.append("start_time", startTimeParam);
      const url = `/comparison-graph?${params.toString()}`;
      console.log("🔄 API Call:", {
        url,
        username,
        dates: selectedDates,
        start_time: startTimeParam,
        force
      });

      const response = await api.get(url);
      console.log("✅ API Response received:", response.data);
      
      if (response.data?.status === "success") {
        console.log("📊 API Response data:", response.data.data);
        setChartData(response.data.data);
      } else {
        console.warn("⚠️ API returned non-success status:", response.data?.status);
        setError("No data available");
        setChartData(null);
      }
    } catch (err) {
      console.error("❌ API Error:", err);
      setError("Failed to load data");
      setChartData(null);
    } finally {
      inFlightFetch.current = false;
      setLoading(false);
    }
  }, [selectedMachine, selectedDates, startHour]);

  // Auto-load disabled: Chart only shows after user clicks "Search"

  const toggleDate = (dateStr) => {
    setSelectedDates((prev) =>
      prev.includes(dateStr)
        ? prev.filter((d) => d !== dateStr)
        : [...prev, dateStr].sort()
    );
  };

  // Close start-time popover when clicking outside
  useEffect(() => {
    if (!isStartOpen) return;
    const onDocClick = (e) => {
      if (startRef.current && !startRef.current.contains(e.target)) {
        setIsStartOpen(false);
      }
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, [isStartOpen]);

  const calendarDays = useMemo(() => {
    const { year, month } = calendarMonth;
    const firstDay = new Date(year, month, 1).getDay();
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const today = new Date().toISOString().split("T")[0];
    const days = [];
    for (let i = 0; i < firstDay; i++) days.push(null);
    for (let d = 1; d <= daysInMonth; d++) {
      const dateStr = `${year}-${String(month + 1).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
      days.push({ day: d, date: dateStr, disabled: dateStr > today, selected: selectedDates.includes(dateStr) });
    }
    return days;
  }, [calendarMonth, selectedDates]);

  const monthLabel = new Date(calendarMonth.year, calendarMonth.month).toLocaleString("default", { month: "long", year: "numeric" });

  const changeMonth = (dir) => {
    setCalendarMonth((prev) => {
      let m = prev.month + dir;
      let y = prev.year;
      if (m < 0) { m = 11; y--; }
      if (m > 11) { m = 0; y++; }
      return { year: y, month: m };
    });
  };

  // Build ApexCharts series from API data
  const { series, categories } = useMemo(() => {
    if (!chartData || !selectedMachine) return { series: [], categories: [] };

    // Use the API timestamps exactly as returned. Do not convert to local/UTC time.
    const apiTimeSlots = chartData[0]?.["Date & Time"] || [];

    const seriesArr = [];

    chartData.forEach((dayData, idx) => {
      const date = dayData.date;
      let machineData = dayData[selectedMachine];

      // If not found directly, handle case-insensitivity and "Usage" suffix stripping
      if (!machineData && selectedMachine) {
        const rawTarget = selectedMachine.trim().toLowerCase();
        const targetWithoutUsage = rawTarget.replace(/\s*usage\s*$/i, "").trim();

        for (const [k, v] of Object.entries(dayData)) {
          if (
            k === "date" ||
            k === "Date & Time" ||
            k === "timestamps" ||
            k === "time_labels" ||
            k === "time"
          )
            continue;
          const keyLower = k.trim().toLowerCase();
          if (keyLower === rawTarget || (targetWithoutUsage && keyLower === targetWithoutUsage)) {
            machineData = v;
            break;
          }
        }
      }

      if (!machineData) {
        // Attempt to aggregate if it's a virtual/branch node
        const node = machinesList.find((m) => m.name === selectedMachine);
        if (node && node.hierarchy_path) {
          const childrenNames = machinesList
            .filter(
              (m) =>
                m.hierarchy_path &&
                m.hierarchy_path.startsWith(node.hierarchy_path) &&
                m.name !== node.name
            )
            .map((m) => m.name);

          if (childrenNames.length > 0) {
            const aggregatedPowerKw = Array(apiTimeSlots.length).fill(0);
            let aggregatedPeakEnergy = 0;
            let aggregatedOffPeakEnergy = 0;
            let hasAnyData = false;

            childrenNames.forEach((childName) => {
              let childData = dayData[childName];
              if (!childData) {
                const raw = childName.trim().toLowerCase();
                for (const [k, v] of Object.entries(dayData)) {
                  if (k.trim().toLowerCase() === raw) {
                    childData = v;
                    break;
                  }
                }
              }

              if (childData) {
                let vals = [];
                if (Array.isArray(childData)) vals = childData;
                else if (childData.power_kw && Array.isArray(childData.power_kw))
                  vals = childData.power_kw;

                if (vals.length > 0) {
                  hasAnyData = true;
                  vals.forEach((val, i) => {
                    if (i < aggregatedPowerKw.length && val != null && !isNaN(parseFloat(val))) {
                      aggregatedPowerKw[i] += parseFloat(val);
                    }
                  });
                }
                
                if (childData.peak_energy && !isNaN(Number(childData.peak_energy))) {
                  aggregatedPeakEnergy += Number(childData.peak_energy);
                }
                if (childData.off_peak_energy && !isNaN(Number(childData.off_peak_energy))) {
                  aggregatedOffPeakEnergy += Number(childData.off_peak_energy);
                }
              }
            });

            if (hasAnyData) {
              machineData = { 
                power_kw: aggregatedPowerKw,
                peak_energy: aggregatedPeakEnergy,
                off_peak_energy: aggregatedOffPeakEnergy
              };
            }
          }
        }
      }

      if (!machineData) {
        console.log(`⚠️ No machine "${selectedMachine}" found in date "${date}"`);
        return;
      }

      // Handle both data formats: direct array OR object with power_kw property
      let machineValues = [];
      if (Array.isArray(machineData)) {
        machineValues = machineData;
      } else if (machineData.power_kw && Array.isArray(machineData.power_kw)) {
        machineValues = machineData.power_kw;
      }

      // Assign consistent color based on date (not index) - same date always gets same color
      const colorIndex = getColorIndexFromDate(date);
      // Use only the date-derived color so colors remain stable across selections
      const color = LINE_COLORS[colorIndex];

      // Keep the API value order exactly as received.
      const pointDateTimes = Array.isArray(dayData["Date & Time"])
        ? dayData["Date & Time"]
        : apiTimeSlots;
      const rawValues = machineValues.slice(0, apiTimeSlots.length).map((v) =>
        v != null ? parseFloat(Number(v).toFixed(2)) : null
      );

      // rawValues stays in kW so the Max/Min Power columns keep reporting the
      // physical peak; only the plotted copy is costed.
      const plotData = isPkr
        ? rawValues.map((v) => (typeof v === "number" ? v * unitPrice : v))
        : rawValues;

      const energy = extractEnergyValue(dayData, machineData, rawValues);
      const estimatedCost = energy * unitPrice;

      // Peak / Off-Peak energy come from the backend per-machine block
      const peakEnergy =
        machineData && Number.isFinite(Number(machineData.peak_energy))
          ? Number(machineData.peak_energy)
          : null;
      const offPeakEnergy =
        machineData && Number.isFinite(Number(machineData.off_peak_energy))
          ? Number(machineData.off_peak_energy)
          : null;

      seriesArr.push({
        name: date,
        data: plotData,
        rawValues,
        pointDateTimes,
        color: color,
        energy,
        estimatedCost,
        peakEnergy,
        offPeakEnergy,
      });
    });

    console.log("=== FINAL SERIES DATA ===");
    console.log("Number of series:", seriesArr.length);
    console.log("Series:", seriesArr);

    return { series: seriesArr, categories: apiTimeSlots };
  }, [chartData, selectedMachine, startHour, unitPrice, isPkr]);

  const Y_AXIS_MAX_LABELS = 5;

  const yAxisScale = useMemo(() => {
    if (!series || series.length === 0) {
      return computeYAxisScale(0, Y_AXIS_MAX_LABELS);
    }

    let maxVal = 0;
    series.forEach((s) => {
      const values = Array.isArray(s.data) ? s.data : s.rawValues;
      if (Array.isArray(values)) {
        values.forEach((val) => {
          if (typeof val === "number" && val > maxVal) {
            maxVal = val;
          }
        });
      }
    });

    return computeYAxisScale(maxVal, Y_AXIS_MAX_LABELS);
  }, [series]);

  const comparisonSummary = useMemo(() => {
    const seriesByDate = new Map(series.map((entry) => [entry.name, entry]));

    return series.map((entry) => {
      const date = entry.name;
      const dayEntry = seriesByDate.get(date) || {};
      const values = (dayEntry.rawValues || []).filter(
        (value) => typeof value === "number" && Number.isFinite(value)
      );
      const energy = Number.isFinite(Number(dayEntry.energy))
        ? Number(dayEntry.energy)
        : null;
      const estimatedCost = Number.isFinite(Number(dayEntry.estimatedCost))
        ? Number(dayEntry.estimatedCost)
        : null;
      const peakEnergy = Number.isFinite(Number(dayEntry.peakEnergy))
        ? Number(dayEntry.peakEnergy)
        : null;
      const offPeakEnergy = Number.isFinite(Number(dayEntry.offPeakEnergy))
        ? Number(dayEntry.offPeakEnergy)
        : null;

      if (values.length === 0) {
        return {
          date,
          max: null,
          min: null,
          energy,
          estimatedCost,
          peakEnergy,
          offPeakEnergy,
        };
      }

      return {
        date,
        max: Math.max(...values),
        min: Math.min(...values),
        energy,
        estimatedCost,
        peakEnergy,
        offPeakEnergy,
      };
    });
  }, [series]);

  const chartOptions = useMemo(() => ({
    chart: {
      type: "line",
      height: 320,
      toolbar: { 
        show: false,
      },
      zoom: { enabled: true },
      animations: { enabled: true, easing: "easeinout", speed: 400 },
    },
    title: {
      text: selectedMachine || title,
      align: "left",
      style: {
        fontSize: "14px",
        fontWeight: 600,
        color: "#475569",
      },
    },
    stroke: { curve: "straight", width: 2.5 },
    markers: {
      size: 4,
      strokeWidth: 1.5,
      hover: { size: 6 },
    },
    xaxis: {
      categories,
      type: "category",
      tickPlacement: "on",
      axisTicks: {
        show: true,
      },
      labels: {
        rotate: -45,
        rotateAlways: true,
        hideOverlappingLabels: false,
        showDuplicates: true,
        trim: false,
        minHeight: 80,
        maxHeight: 120,
        style: {
          fontSize: "10px",
        },
        formatter: (val) => formatTimeOnlyLabel(val),
      },
    },
    yaxis: {
      min: 0,
      max: yAxisScale.max,
      stepSize: yAxisScale.stepSize,
      forceNiceScale: false,
      decimalsInFloat: 0,
      labels: {
        formatter: (val) => {
          const num = Number(val);
          if (!Number.isFinite(num)) return "";
          if (
            !isAllowedYTick(num, yAxisScale.stepSize, yAxisScale.max)
          ) {
            return "";
          }

          const displayValue =
            Math.abs(num) >= 100
              ? Math.round(num)
              : Math.round(num * 10) / 10;
          return `${displayValue} ${seriesUnit}`;
        },
      },
    },
    tooltip: {
      shared: true,
      intersect: false,
      x: {
        formatter: (val, opts) => {
          const dataPointIndex = opts?.dataPointIndex;
          const seriesIndex = opts?.seriesIndex;
          const pointDateTimes = opts?.w?.config?.series?.[seriesIndex]?.pointDateTimes;

          if (
            Number.isInteger(dataPointIndex) &&
            Array.isArray(pointDateTimes) &&
            dataPointIndex >= 0 &&
            dataPointIndex < pointDateTimes.length
          ) {
            return formatTimeOnlyLabel(pointDateTimes[dataPointIndex]);
          }

          const tooltipLabels = opts?.w?.globals?.labels;

          // Always resolve from category labels so tooltip text matches x-axis text exactly.
          if (
            Number.isInteger(dataPointIndex) &&
            Array.isArray(tooltipLabels) &&
            dataPointIndex >= 0 &&
            dataPointIndex < tooltipLabels.length
          ) {
            return tooltipLabels[dataPointIndex] ?? "";
          }

          return typeof val === "string" ? val : String(val ?? "");
        },
      },
      y: {
        formatter: (val) =>
          val != null ? `${formatAmount(val)} ${seriesUnit}` : "N/A",
        title: {
          formatter: (seriesName, opts) => {
            const seriesIndex = opts?.seriesIndex;
            const dataPointIndex = opts?.dataPointIndex;
            const pointDateTimes = opts?.w?.config?.series?.[seriesIndex]?.pointDateTimes;

            if (
              Number.isInteger(dataPointIndex) &&
              Array.isArray(pointDateTimes) &&
              dataPointIndex >= 0 &&
              dataPointIndex < pointDateTimes.length
            ) {
              return `${extractDateOnlyLabel(pointDateTimes[dataPointIndex])}:`;
            }

            return `${seriesName}:`;
          },
        },
      },
    },
    dataLabels: {
      enabled: false,
    },
    legend: {
      position: "bottom",
      fontSize: "12px",
      markers: { size: 6 },
    },
    grid: {
      borderColor: "#e2e8f0",
      strokeDashArray: 4,
    },
    // Use per-series colors so the first plotted series appears as the primary green
    colors: series && series.length > 0 ? series.map((s) => s.color) : LINE_COLORS,
  }), [categories, title, selectedMachine, yAxisScale, series, seriesUnit]);

  // Auto-load the default view once the initial machine is available.
  useEffect(() => {
    if (hasAutoLoadedRef.current) return;
    if (!selectedMachine || selectedDates.length === 0) return;

    hasAutoLoadedRef.current = true;
    fetchComparisonData(true, startHour);
  }, [selectedMachine, selectedDates, startHour, fetchComparisonData]);

  const chartDataExport = {
    labels: categories,
    datasets: series.map((s) => ({ label: s.name, data: s.data || [] })),
    unit: seriesUnit,
  };

  return (
    <div 
      ref={chartRef} 
      style={{
        ...styles.wrapper, 
        ...(isFullscreen ? {
          borderRadius: "0", 
          border: "none", 
          boxShadow: "none",
          display: "flex",
          flexDirection: "column",
          width: "100%",
          height: "100%"
        } : {})
      }}
    >
      <div style={{ ...styles.header, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <h3 style={styles.title}>{title}</h3>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <AIAnalyzerButton onClick={handleAnalyzeComparisonGraph} />
          <ChartBurgerMenu 
            chartRef={chartRef} 
            chartTitle={"Comparison_" + (selectedMachine || title)} 
            onAIAnalyze={handleAnalyzeComparisonGraph}
            chartData={series.length > 0 ? chartDataExport : null} 
            exportContainer={true} 
            chartType="comparison"
          />
        </div>
      </div>

      <div style={styles.controls}>
        {/* Inline Calendar */}
        <div style={{ ...styles.controlGroup, position: "relative" }}>
          <label style={styles.label}>Select multiple dates</label>
          <button
            onClick={() => setIsCalendarOpen(!isCalendarOpen)}
            style={{ ...styles.select, textAlign: "left", background: "#fff", display: "flex", justifyContent: "space-between", alignItems: "center" }}
          >
          <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#1e293b" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
              <line x1="16" y1="2" x2="16" y2="6"></line>
              <line x1="8" y1="2" x2="8" y2="6"></line>
              <line x1="3" y1="10" x2="21" y2="10"></line>
            </svg>
            <span style={{ fontWeight: 500, color: "#1e293b" }}>Select Date</span>
          </div>
            <span style={{ fontSize: "10px", marginLeft: "8px" }}>▼</span>
          </button>
          
          {isCalendarOpen && (
          <div style={{ ...styles.calendar, position: "absolute", top: "100%", left: 0, marginTop: "4px", zIndex: 50, boxShadow: "0 10px 15px -3px rgba(0, 0, 0, 0.1)" }}>
            <div style={styles.calHeader}>
              <button onClick={() => changeMonth(-1)} style={styles.calNav}>&lt;</button>
              <span style={styles.calMonth}>{monthLabel}</span>
              <button onClick={() => changeMonth(1)} style={styles.calNav}>&gt;</button>
            </div>
            <div style={styles.calWeekRow}>
              {["Su","Mo","Tu","We","Th","Fr","Sa"].map((d) => (
                <span key={d} style={styles.calWeekDay}>{d}</span>
              ))}
            </div>
            <div style={styles.calGrid}>
              {calendarDays.map((cell, i) =>
                cell === null ? (
                  <span key={`e${i}`} style={styles.calCell}></span>
                ) : (
                  <button
                    key={cell.date}
                    disabled={cell.disabled}
                    onClick={() => toggleDate(cell.date)}
                    style={{
                      ...styles.calCell,
                      ...styles.calDayBtn,
                      ...(cell.selected ? styles.calDaySelected : {}),
                      ...(cell.disabled ? styles.calDayDisabled : {}),
                    }}
                  >
                    {cell.day}
                  </button>
                )
              )}
            </div>
          </div>
          )}
          <button
            onClick={() => {
              fetchComparisonData(true);
              setIsCalendarOpen(false);
            }}
            style={{
              marginTop: 8,
              padding: "8px 16px",
              backgroundColor: "#1BA08E",
              color: "#fff",
              border: "1px solid #1BA08E",
              borderRadius: "4px",
              fontSize: "13px",
              fontWeight: 600,
              cursor: "pointer",
              width: "100%",
            }}
          >
            Search
          </button>
        </div>

        {/* Start time selector (custom popover to force downward opening) */}
        <div style={{ ...styles.controlGroup, position: "relative" }} ref={startRef}>
          <label style={styles.label}>Start Time:</label>
          <button
            type="button"
            onClick={() => setIsStartOpen((s) => !s)}
            style={{ ...styles.select, display: "flex", justifyContent: "space-between", alignItems: "center", background: "#fff" }}
          >
            <span>{`${String(startHour).padStart(2, "0")} :00:00`.replace(" ", "")}</span>
            <span style={{ marginLeft: 8, fontSize: 12 }}>▾</span>
          </button>

          {isStartOpen && (
            <div
              style={{
                position: "absolute",
                top: "100%",
                left: 0,
                marginTop: 6,
                width: 220,
                maxHeight: 240,
                overflowY: "auto",
                border: "1px solid #cbd5e1",
                borderRadius: 8,
                background: "#fff",
                zIndex: 80,
                boxShadow: "0 10px 15px -3px rgba(0,0,0,0.08)",
              }}
            >
              {Array.from({ length: 24 }, (_, i) => (
                <div
                  key={i}
                  onClick={() => {
                    setStartHour(i);
                    setIsStartOpen(false);
                  }}
                  style={{
                        padding: "8px 12px",
                        cursor: "pointer",
                        background: i === startHour ? "#eef2ff" : "#fff",
                        color: "#111827",
                        fontSize: "11px",
                        lineHeight: 1.2,
                  }}
                >
                  {`${String(i).padStart(2, "0")} :00:00`.replace(" ", "")}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Machine selector */}
        <div style={styles.controlGroup}>
          <label style={styles.label}>Select a Load:</label>
          <select
            value={selectedMachine}
            onChange={(e) => setSelectedMachine(e.target.value)}
            style={styles.select}
          >
            {loadOptions.map((m) => (
              <option key={m.name} value={m.name}>
                {m.name === "Usage" ? "Usage (Total Consumption)" : m.name}
                {m.category && m.category !== "SPECIAL" && m.category !== "UNKNOWN" && m.name !== "Usage"
                  ? ` (${m.category})`
                  : ""}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Chart area */}
      <div style={{ ...styles.chartArea, ...(isFullscreen ? { flex: 1, display: "flex", flexDirection: "column" } : {}) }}>
        {loading && (
          <BoltLoader label={`Loading ${title || "comparison"} data\u2026`} />
        )}
        {error && !loading && (
          <div style={styles.noData}>{error}</div>
        )}
        {!error && series.length > 0 && (
          <div style={{ flex: 1, minHeight: 0 }}>
            <ReactApexChart
              options={chartOptions}
              series={series}
              type="line"
              height={isFullscreen ? "100%" : 320}
              width="100%"
            />
          </div>
        )}
        {!error && series.length === 0 && !loading && (
          <div style={styles.noData}>
            {selectedDates.length === 0
              ? "Please select at least one date and click Search"
              : "Click Search to load comparison data"}
          </div>
        )}
      </div>

      {chartData && comparisonSummary.length > 0 && (
      <div style={styles.summarySection}>
        <div style={styles.summaryHeaderRow}>
          <h4 style={styles.summaryTitle}>Summary</h4>
        </div>

        <div style={styles.summaryTableWrap}>
          <table style={styles.summaryTable}>
            <thead>
              <tr>
                <th style={styles.summaryTh}>Date</th>
                <th style={styles.summaryTh}>Energy ({energyUnit})</th>
                {/* In PKR the Energy column already is the cost, so the
                    separate Estimated Cost column would just repeat it. */}
                {!isPkr && (
                  <th style={styles.summaryTh}>Estimated Cost (PKR)</th>
                )}
                <th style={styles.summaryTh}>Peak ({energyUnit})</th>
                <th style={styles.summaryTh}>Off-Peak ({energyUnit})</th>
                <th style={styles.summaryTh}>Max Power (kW)</th>
                <th style={styles.summaryTh}>Min Power (kW)</th>
              </tr>
            </thead>
            <tbody>
              {comparisonSummary.map((row) => (
                <tr key={row.date}>
                  <td style={styles.summaryTd}>{row.date}</td>
                  <td style={styles.summaryTd}>
                    {row.energy == null
                      ? "N/A"
                      : formatAmount(row.energy * (isPkr ? unitPrice : 1))}
                  </td>
                  {!isPkr && (
                    <td style={styles.summaryTd}>{row.estimatedCost == null ? "N/A" : formatAmount(row.estimatedCost)}</td>
                  )}
                  {/* Peak and off-peak hours bill at their own grid rates. */}
                  <td style={styles.summaryTd}>
                    {row.peakEnergy == null
                      ? "N/A"
                      : formatAmount(row.peakEnergy * (isPkr ? rates.peak : 1))}
                  </td>
                  <td style={styles.summaryTd}>
                    {row.offPeakEnergy == null
                      ? "N/A"
                      : formatAmount(row.offPeakEnergy * (isPkr ? rates.offPeak : 1))}
                  </td>
                  <td style={styles.summaryTd}>{row.max == null ? "N/A" : formatAmount(row.max)}</td>
                  <td style={styles.summaryTd}>{row.min == null ? "N/A" : formatAmount(row.min)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      )}
      <AIAnalyzerModal
        isOpen={aiModalOpen}
        onClose={() => setAiModalOpen(false)}
        chartPayload={aiModalPayload}
      />
    </div>
  );
};

const styles = {
  wrapper: {
    background: "#fff",
    borderRadius: 16,
    border: "1px solid #e2e8f0",
    boxShadow: "0 2px 12px rgba(0,0,0,0.06)",
    padding: 20,
    flex: "1 1 48%",
    minWidth: 400,
  },
  header: {
    marginBottom: 12,
  },
  title: {
    margin: 0,
    fontSize: 16,
    fontWeight: 700,
    color: "#1e293b",
  },
    controls: {
    display: "flex",
    gap: 16,
    alignItems: "flex-start",
    flexWrap: "wrap",
    marginBottom: 12,
    paddingRight: "8px",
  },
  controlGroup: {
    display: "flex",
    flexDirection: "column",
    gap: 6,
  },
  label: {
    fontSize: 12,
    fontWeight: 600,
    color: "#475569",
  },
  calendar: {
    border: "1px solid #e2e8f0",
    borderRadius: 10,
    padding: 8,
    background: "#fff",
    width: 230,
  },
  calHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: 6,
  },
  calNav: {
    background: "none",
    border: "none",
    cursor: "pointer",
    fontSize: 14,
    fontWeight: 700,
    color: "#475569",
    padding: "2px 8px",
    borderRadius: 4,
  },
  calMonth: {
    fontSize: 13,
    fontWeight: 600,
    color: "#1e293b",
  },
  calWeekRow: {
    display: "grid",
    gridTemplateColumns: "repeat(7, 1fr)",
    textAlign: "center",
    marginBottom: 2,
  },
  calWeekDay: {
    fontSize: 10,
    fontWeight: 600,
    color: "#94a3b8",
    padding: 2,
  },
  calGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(7, 1fr)",
    gap: 2,
  },
  calCell: {
    width: 28,
    height: 28,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    margin: "0 auto",
  },
  calDayBtn: {
    border: "none",
    borderRadius: "50%",
    background: "transparent",
    fontSize: 12,
    fontWeight: 500,
    color: "#334155",
    cursor: "pointer",
    transition: "all 0.15s",
  },
  calDaySelected: {
    background: "#1BA08E",
    color: "#fff",
    fontWeight: 700,
  },
  calDayDisabled: {
    color: "#cbd5e1",
    cursor: "default",
  },
  select: {
    padding: "8px 12px",
    borderRadius: 8,
    border: "1px solid #cbd5e1",
    fontSize: 13,
    outline: "none",
    minWidth: 180,
    cursor: "pointer",
  },
  chartArea: {
    position: "relative",
    minHeight: 320,
  },
  overlay: {
    position: "absolute",
    top: 0, left: 0, right: 0, bottom: 0,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    gap: 8,
    background: "rgba(255,255,255,0.8)",
    zIndex: 5,
    borderRadius: 12,
  },
  spinner: {
    width: 28,
    height: 28,
    border: "3px solid #e2e8f0",
    borderTop: "3px solid #1BA08E",
    borderRadius: "50%",
    animation: "spin 0.8s linear infinite",
  },
  noData: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    height: 320,
    color: "#94a3b8",
    fontSize: 14,
  },
  summarySection: {
    marginTop: 16,
    padding: 0,
    border: "none",
    borderRadius: 0,
    background: "transparent",
  },
  summaryHeaderRow: {
    display: "flex",
    flexDirection: "column",
    gap: 4,
    marginBottom: 12,
  },
  summaryTitle: {
    margin: 0,
    fontSize: 14,
    fontWeight: 700,
    color: "#1e293b",
  },
  summaryTableWrap: {
    overflowX: "auto",
  },
  summaryTable: {
    width: "100%",
    borderCollapse: "collapse",
    background: "#fff",
    borderRadius: 10,
    overflow: "hidden",
  },
  summaryTh: {
    textAlign: "left",
    padding: "10px 12px",
    fontSize: 12,
    fontWeight: 700,
    color: "#475569",
    background: "#eef2f7",
    borderBottom: "1px solid #e2e8f0",
    whiteSpace: "nowrap",
  },
  summaryTd: {
    padding: "10px 12px",
    fontSize: 13,
    color: "#334155",
    borderBottom: "1px solid #e2e8f0",
    whiteSpace: "nowrap",
  },
};

export default ComparisonGraph;
